import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { admin } from '../../api/client.js';
import { EmptyState, Badge } from '../../components/admin/AdminUI.jsx';

/**
 * Who in this organisation has opened which report.
 *
 * The portal has recorded a row per report, dashboard and website open since
 * it launched; until now nothing read it back. See db/014_page_views.sql.
 *
 * Scoped server-side to the caller's own customer. There is no query string
 * here that could ask for another organisation's figures.
 *
 * Two presentation decisions worth knowing.
 *
 * The default sort is by PEOPLE, not views. Forty views is one person
 * refreshing all morning or forty people looking once, and those mean opposite
 * things when you are deciding whether a report earns its keep.
 *
 * And "Not opened" is given equal billing with what IS used. A list of popular
 * reports is mildly interesting; a list of reports nobody has opened in three
 * months is the one that leads to a decision.
 */
export default function AdminUsage() {
  const [days, setDays] = useState(30);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'usage', days],
    queryFn: () => admin.getUsage(days)
  });

  if (isLoading) return <div className="admin-page"><div className="spinner-border" /></div>;

  if (isError) {
    return (
      <div className="admin-page">
        <h1 className="h4 mb-3">Report usage</h1>
        <div className="alert alert-warning mb-0">
          Could not load usage. If this persists, the view may not have been
          created yet — check <code>/readyz</code> for <code>014_page_views.sql</code>.
        </div>
      </div>
    );
  }

  const byItem = data?.byItem ?? [];
  const byUser = data?.byUser ?? [];
  const unused = data?.unused ?? [];
  const peak = Math.max(...byItem.map(i => i.distinctUsers ?? 0), 0);

  return (
    <div className="admin-page">
      <div className="d-flex align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Report usage</h1>
          <p className="text-secondary small mb-0">
            What your organisation has opened, and what it has not.
          </p>
        </div>
        <div className="ms-auto">
          <select className="form-select form-select-sm" style={{ width: 'auto' }}
                  value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
        </div>
      </div>

      {byItem.length === 0 && unused.length === 0 ? (
        <EmptyState icon="bi-graph-up" title="Nothing recorded yet">
          No reports have been opened in this period.
        </EmptyState>
      ) : (
        <>
          <h2 className="h6 text-secondary mb-2">Most used</h2>
          {byItem.length === 0
            ? <p className="text-secondary small">Nothing opened in this period.</p>
            : (
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="text-end">People</th>
                    <th className="text-end">Opens</th>
                    <th className="text-end">Last opened</th>
                    <th style={{ width: '22%' }} />
                  </tr>
                </thead>
                <tbody>
                  {byItem.map(i => (
                    <tr key={`${i.itemType}-${i.targetId}`}>
                      <td>
                        <div className="fw-medium">{i.itemName}</div>
                        <div className="admin-muted small">
                          {i.itemType}
                          {/* Surfaced rather than hidden: an item that has left
                              the catalogue is usually a deletion nobody
                              announced, and this is where it shows up. */}
                          {!i.stillInCatalogue && (
                            <Badge tone="warning" title="No longer in the Power BI catalogue">
                              removed
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="text-end fw-medium">{i.distinctUsers}</td>
                      <td className="text-end text-secondary">{i.views}</td>
                      <td className="text-end text-secondary small">{when(i.lastViewUtc)}</td>
                      <td>
                        <div className="bg-body-secondary rounded" style={{ height: 8 }}>
                          <div className="rounded" style={{
                            height: 8,
                            width: peak > 0 ? `${Math.round((i.distinctUsers / peak) * 100)}%` : '0%',
                            background: 'var(--vz-magenta)'
                          }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          <div className="row g-4 mt-1">
            <div className="col-lg-6">
              <h2 className="h6 text-secondary mb-2">
                Not opened in this period
              </h2>
              {unused.length === 0
                ? <p className="text-secondary small">
                    Everything in your menus has been opened at least once.
                  </p>
                : (
                  <>
                    <p className="text-secondary small">
                      In someone&rsquo;s menu, but nobody has opened it. Worth a look
                      before it is renewed or rebuilt.
                    </p>
                    <ul className="list-unstyled mb-0">
                      {unused.map(i => (
                        <li key={`${i.itemType}-${i.targetId}`} className="mb-1">
                          <span className="fw-medium">{i.itemName || i.targetId}</span>{' '}
                          <span className="admin-muted small">{i.itemType}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
            </div>

            <div className="col-lg-6">
              <h2 className="h6 text-secondary mb-2">By person</h2>
              {byUser.length === 0
                ? <p className="text-secondary small">Nobody has opened anything.</p>
                : (
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th className="text-end">Opens</th>
                        <th className="text-end">Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byUser.map(u => (
                        <tr key={u.userId ?? u.email}>
                          <td className="small">{u.email}</td>
                          <td className="text-end">{u.views}</td>
                          <td className="text-end text-secondary">{u.distinctItems}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** Relative for anything recent, absolute once it stops being memorable. */
function when(utc) {
  if (!utc) return '—';
  const then = new Date(utc);
  const hours = (Date.now() - then.getTime()) / 3_600_000;
  if (hours < 1) return 'just now';
  if (hours < 24) return `${Math.floor(hours)}h ago`;
  if (hours < 24 * 14) return `${Math.floor(hours / 24)}d ago`;
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
